import { type ReactElement, type ReactNode } from 'react';
import { Box, Card, CardContent, FeatureErrorBoundary } from '@citibox-panel/ui';
import { PageHeader } from '../../../components/PageHeader';
import { PageIntro } from './PageIntro';
import { CoverageCard } from './CoverageCard';
import { PermissionSummary } from './PermissionSummary';
import { BatchCard } from './BatchCard';
import { LookupCard } from './LookupCard';
import { type DoorAccessLocation } from '../lib/doorAccessLocation';

export interface AmazonDoorAccessPageProps {
  brandLabel: string;
  brandDegraded: boolean;
  headerEnd?: ReactNode;
  // Injected by the host, which is what picks mock or real: the slice carries no client and no env
  // wiring, and therefore no dependency on the api-client at all.
  fetchDoorAccessCount: (enabled: boolean) => Promise<number>;
  setDoorAccess: (locationIds: number[], enabled: boolean) => Promise<void>;
  fetchDoorAccessLocation: (locationId: number) => Promise<DoorAccessLocation>;
  // The BARE market label the confirmation names («ES»), NOT derivable from `brandLabel` — that one
  // is the brand pill's whole text («Soporte ES»). `null` is a deployment that cannot be trusted to
  // name its market. The host resolves both; this module derives neither.
  marketLabel: string | null;
}

// The screen hangs off no navigation and is reached by direct URL, so it carries its own chrome
// rather than borrowing a shell's. Everything in that chrome is filled by the host: this module
// resolves no config and knows no route.
//
// It opens no `main` landmark: the host's authenticated layout already does, and a second one would
// put two mains in the document.
export function AmazonDoorAccessPage({
  brandLabel,
  brandDegraded,
  headerEnd,
  fetchDoorAccessCount,
  setDoorAccess,
  fetchDoorAccessLocation,
  marketLabel,
}: AmazonDoorAccessPageProps): ReactElement {
  return (
    <>
      {/* Full-bleed and sticky: the band spans the viewport, so it sits OUTSIDE the inset the
          panel below takes. */}
      <PageHeader brandLabel={brandLabel} brandDegraded={brandDegraded} headerEnd={headerEnd} />
      {/* One white, rounded panel for everything the page itself says — same surface, same `m: 2`
          inset and same default `CardContent` padding as every other card at this level (the
          «Entrega» detail, the «Usuario» ficha, the location list). It is what the band above
          deliberately is NOT: chrome spans the viewport, content sits on an inset sheet. Outlined
          rather than elevated, per the house pattern. */}
      <Card variant="outlined" sx={{ borderRadius: 3, m: 2 }}>
        <CardContent>
          <PageIntro />
          {/* Two columns on the 8px scale. DOM order is the reading order and the tab order, and
              the cards that mount here later fill the empty column without moving this one. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            {/* Per-card boundary: a card that throws takes itself and nothing else. The chrome —
                and with it the only way to sign out of this screen — has to survive whatever a
                card does. */}
            {/* The sentence sits in the counter's column, above the number it explains — and
                OUTSIDE the boundary, because static copy has nothing to throw and a card that does
                must not take the words that say what the screen is for. */}
            <Box>
              <PermissionSummary />
              <FeatureErrorBoundary>
                <CoverageCard fetchCount={fetchDoorAccessCount} />
              </FeatureErrorBoundary>
              {/* The grid's gap separates COLUMNS, not what sits inside one: the 24px between this
                  column's two cards has to be put here for the rhythm to match on both axes. */}
              <Box sx={{ mt: 3 }}>
                <FeatureErrorBoundary>
                  <LookupCard fetchLocation={fetchDoorAccessLocation} />
                </FeatureErrorBoundary>
              </Box>
            </Box>
            <FeatureErrorBoundary>
              <BatchCard marketLabel={marketLabel} setDoorAccess={setDoorAccess} />
            </FeatureErrorBoundary>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}
